import React, {
    memo,
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import HardBreak from '@tiptap/extension-hard-break';
import { TextStyleKit } from '@tiptap/extension-text-style';
import Link from '@tiptap/extension-link';
import {
    getOverrides,
    useClasses,
    createUseStyles,
    useTheme,
    InputWrapper,
    Icon,
} from '@Sage/hoi-poi-ui';

import Image from './components/Image';
import Emoji from './components/emojiExtension.js';
import Placeholder from './components/placeholderExtension';
import Menu from './components/Menu';
import { getEmojiConfig, getSuggestionsConfig } from './components/extensionsConfig';
import Variable from './components/Variable';

import styles from './styles';
import { isEditorViewReady, patchEditorForSafeFocus } from './editorUtils';

const FREQUENTLY_USED_ITEMS_TO_SHOW = 36;

const useStyles = createUseStyles(styles, 'RichText');

export const RichTextContext = createContext();

const RichText = memo(
    ({
        className: classNameProp,
        classes: classesProp,
        error,
        info,
        onSubmit,
        onEsc,
        overrides: overridesProp,
        placeholder,
        mention,
        customActions,
        canSubmit,
        onChangeFocus,
        emoji,
        variables,
        isBasic,
        getEditorRef,

        isFullWidth = false,
        isReadOnly = false,
        onBlur = () => {},
        onChange = () => {},
        onClick = () => {},
        onFocus = () => {},
        onCreate = () => {},
        toolbar = [
            {
                hint: 'Bold',
                item: 'b',
            },
            {
                hint: 'Italic',
                item: 'i',
            },
            {
                hint: 'Underline',
                item: 'u',
            },
            {
                hint: 'Strike',
                item: 'del',
            },
            {
                hint: 'Unordered List',
                item: 'ul',
            },
            {
                hint: 'Ordered List',
                item: 'ol',
            },
        ],
        toolbarStyle = 'floating',
        value = '',
        compactMode = false,
        autofocus = false,
        hideClear = false,
        submitTooltip = 'Send ⏎',
        basicType = 'dynamic',
        loading = false,
        isSubmitDisabled = false,
        withCustomToolbar = false,
        chatbox = false,
        linkConfig = {
            openOnClick: true,
            autolink: true,
            shouldAutoLink: (href) => /^https?:\/\//.test(href),
        },
        bottomComponent,
        preComponent,
        topComponent,
        ...otherProps
    }) => {
        const theme = useTheme();
        const classes = useClasses(useStyles, classesProp);
        const override = getOverrides(overridesProp, RichText.overrides);
        const [focused, setFocused] = useState(false);
        const [showingMenuPopover, setShowingMenuPopover] = useState(false);
        const [editorContent, setEditorContent] = useState({});
        const showingMention = useRef(false);
        const showingEmoji = useRef(false);
        const editorDivRef = useRef(null);
        const placeholderRef = useRef(placeholder);

        // Update placeholder ref on every render to support dynamic updates
        placeholderRef.current = placeholder;

        const getExtensions = useMemo(() => {
            const extensions = [
                Placeholder.configure({
                    placeholder: () => placeholderRef.current || null,
                }),
                StarterKit.configure({
                    hardBreak: !chatbox,
                }),
                TextStyleKit.configure({
                    backgroundColor: {
                        types: ['textStyle'],
                    },
                    color: {
                        types: ['textStyle'],
                    },
                    fontFamily: {
                        types: ['textStyle'],
                    },
                    fontSize: {
                        types: ['textStyle'],
                    },
                    lineHeight: {
                        types: ['textStyle'],
                    },
                }),
                Link.configure(linkConfig),
                Image.configure(),
            ];

            if (chatbox) {
                extensions.push(
                    HardBreak.extend({
                        addKeyboardShortcuts() {
                            const handleShiftEnter = () => {
                                if (
                                    this.editor.isActive('bulletList') ||
                                    this.editor.isActive('orderedList')
                                ) {
                                    this.editor.commands.first(({ commands }) => [
                                        () => commands.splitListItem('listItem'),
                                    ]);
                                } else {
                                    this.editor.commands.first(({ commands }) => [
                                        () => commands.newlineInCode(),
                                        () => commands.createParagraphNear(),
                                        () => commands.liftEmptyBlock(),
                                        () => commands.splitBlock(),
                                    ]);
                                }
                            };

                            return {
                                Enter: () => true, // Prevent extra line-break on submiting via Enter
                                'Shift-Enter': handleShiftEnter,
                            };
                        },
                    }),
                );
            }

            if (emoji) {
                extensions.push(
                    Emoji.configure({
                        enableEmoticons: true,
                        suggestion: getEmojiConfig({
                            emoji,
                            showingEmoji,
                        }),
                    }),
                );
            }

            if (
                mention &&
                mention.fetchSuggestions &&
                typeof mention.fetchSuggestions === 'function'
            ) {
                extensions.push(
                    Mention.configure({
                        suggestion: getSuggestionsConfig({ mention, showingMention }),
                        deleteTriggerWithBackspace: true,
                        renderText({ node }) {
                            return `${node.attrs.label ?? node.attrs.id}`;
                        },
                    }),
                );
            }

            if (variables) extensions.push(Variable.configure({ variableMap: variables }));

            return extensions;
        }, [chatbox, emoji, linkConfig, mention, variables]);

        const editor = useEditor({
            editable: !isReadOnly,
            extensions: getExtensions,
            content: value,
            onCreate: ({ editor }) => {
                patchEditorForSafeFocus(editor);

                const content = {
                    text: editor.getText(),
                    html: editor.getHTML(),
                    json: editor.getJSON(),
                };

                setEditorContent(content);

                if (content.text.length && autofocus) {
                    setFocused(true);
                    editor.commands.focus('end');
                }
                onCreate?.(content);
            },
            onUpdate: ({ editor }) => {
                const content = {
                    text: editor.getText(),
                    html: editor.getHTML(),
                    json: editor.getJSON(),
                };
                setEditorContent(content);
                onChange?.(content);
            },
            onFocus: ({ event }) => {
                setFocused(true);
                onFocus?.(event);
                onChangeFocus?.(true);
            },
            onBlur: ({ event }) => {
                if (compactMode) return;
                setFocused(false);
                onBlur?.(event);
                onChangeFocus?.(false);
            },
        });

        useEffect(() => {
            if (!editor) return;
            patchEditorForSafeFocus(editor);
            getEditorRef?.(editor);
        }, [editor, getEditorRef]);

        // Force editor update when placeholder changes to refresh decorations
        useEffect(() => {
            if (isEditorViewReady(editor)) {
                // Dispatch an empty transaction to force a state update and recompute decorations
                editor.view.dispatch(editor.state.tr.setMeta('addToHistory', false));
            }
        }, [editor, placeholder]);

        useEffect(() => {
            if (autofocus && editor && !showingMenuPopover) {
                editor.commands.focus('end');
                setFocused(true);
            }
        }, [autofocus, editor, showingMenuPopover]);

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (!editorDivRef?.current.contains(event.target) && !showingMenuPopover) {
                    setFocused(false);
                    onBlur && onBlur(event);
                    onChangeFocus && onChangeFocus(false);
                }
            };
            document.addEventListener('click', handleClickOutside, true);
            return () => {
                document.removeEventListener('click', handleClickOutside, true);
            };
        }, [onBlur, onChangeFocus, showingMenuPopover]);

        const handleEmojiCache = useCallback(
            (name) => {
                if (!emoji?.cache && !emoji?.saveCache) return;
                let newCache = { ...emoji.cache() };
                newCache[name] = newCache[name] ? newCache[name] + 1 : 1;
                newCache = Object.fromEntries(
                    Object.entries(newCache)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, FREQUENTLY_USED_ITEMS_TO_SHOW),
                );
                emoji.saveCache(newCache);
            },
            [emoji],
        );

        const handleClear = useCallback(
            (e) => {
                e.stopPropagation();
                editor.commands.clearContent(true);
                editor.commands.focus();
            },
            [editor],
        );

        const handleClick = useCallback(
            (e) => {
                if (compactMode) e.stopPropagation();
                if (!focused && !isReadOnly && !showingMenuPopover) {
                    editor.commands.focus();
                    setFocused(true);
                }
                onClick && onClick(e);
            },
            [editor, focused, isReadOnly, onClick, compactMode, showingMenuPopover],
        );

        const handleSubmit = useCallback(() => {
            if (!onSubmit) {
                // Standard enter behavior: new paragraph
                editor.commands.splitBlock();
            } else {
                onSubmit();
                editor.commands.blur();
                editor.commands.clearContent(true);
            }
        }, [editor, onSubmit]);

        const handleKeyDown = useCallback(
            (e) => {
                if (
                    e.key === 'Escape' &&
                    !showingMention.current &&
                    !showingEmoji.current &&
                    !showingMenuPopover
                ) {
                    editor.commands.blur();
                    setFocused(false);
                    onEsc && onEsc(e);
                }

                if (e.key === 'Escape' && (showingMention.current || showingEmoji.current)) {
                    showingMention.current = false;
                    showingEmoji.current = false;
                }

                if (e.key === 'Tab' && (showingEmoji.current || showingMenuPopover)) {
                    showingEmoji.current = false;
                    return;
                }

                if (chatbox && e.key === 'Enter' && !e.shiftKey) {
                    if (showingMention.current || showingEmoji.current || showingMenuPopover) {
                        showingMention.current = false;
                        showingEmoji.current = false;
                        return;
                    }
                    if (loading || isSubmitDisabled || (canSubmit && !canSubmit(editorContent)))
                        return;
                    handleSubmit();
                }
            },
            [
                editor,
                onEsc,
                isSubmitDisabled,
                canSubmit,
                editorContent,
                handleSubmit,
                showingMenuPopover,
                loading,
                chatbox,
            ],
        );

        const getIcons = useMemo(() => {
            switch (true) {
                case editorContent?.text && !isReadOnly && !compactMode && !isBasic && !hideClear:
                    return (
                        <Icon
                            name="closeSmall"
                            size="large"
                            className={classes.actionIcon}
                            onClick={handleClear}
                        />
                    );
                case isReadOnly:
                    return (
                        <Icon
                            name="lockOutline"
                            size="large"
                            className={classes.actionIcon}
                            color={theme.colors.grey[500]}
                        />
                    );
                default:
                    return null;
            }
        }, [
            classes.actionIcon,
            editorContent?.text,
            handleClear,
            isReadOnly,
            theme,
            compactMode,
            hideClear,
            isBasic,
        ]);

        const hasTopComponent = useMemo(() => {
            return !!topComponent;
        }, [topComponent]);

        const inputWrapperProps = useMemo(
            () => ({
                className: classNames(
                    classes.root,
                    {
                        [classes.focused]: focused,
                        [classes.error]: error,
                        [classes.isFullWidth]: isFullWidth,
                        [classes.isReadOnly]: isReadOnly,
                        [classes.hasFixedToolbar]: toolbarStyle === 'fixed',
                        [classes.compactMode]: !isBasic && compactMode && !focused,
                        [classes.compactModeFocused]:
                            !isBasic && compactMode && (focused || !!editorContent?.text?.length),
                        [classes.isBasic]: isBasic,
                        [classes.basicStatic]: basicType === 'static',
                        [classes.hasTopComponent]: hasTopComponent,
                    },
                    classNameProp,
                ),
                error,
                info,
                ...otherProps,
                ...override.root,
            }),
            [
                classNameProp,
                classes,
                compactMode,
                editorContent,
                error,
                focused,
                info,
                isFullWidth,
                isReadOnly,
                otherProps,
                toolbarStyle,
                isBasic,
                basicType,
                override,
                hasTopComponent,
            ],
        );

        const editorWrapperProps = useMemo(
            () => ({
                className: classes.editorWrapper,
                onClick: handleClick,
                onKeyDown: handleKeyDown,
                ref: editorDivRef,
                ...override.editorWrapper,
            }),
            [classes, handleClick, override, handleKeyDown],
        );

        const editorProps = useMemo(
            () => ({
                editor,
                className: classes.editor,
                ...override.editor,
            }),
            [classes, editor, override],
        );

        const menuProps = useMemo(
            () => ({
                canSubmit,
                compactMode,
                customActions,
                editorContent,
                focused,
                handleSubmit,
                toolbar,
                toolbarStyle,
                submitTooltip,
                isBasic,
                loading,
                isSubmitDisabled,
            }),
            [
                canSubmit,
                compactMode,
                customActions,
                editorContent,
                focused,
                handleSubmit,
                toolbar,
                toolbarStyle,
                submitTooltip,
                isBasic,
                loading,
                isSubmitDisabled,
            ],
        );

        const topComponentProps = useMemo(
            () => ({
                className: classes.topComponent,
                ...override.topComponent,
            }),
            [classes, override],
        );

        const bottomComponentProps = useMemo(
            () => ({
                className: classes.bottomComponent,
                ...override.bottomComponent,
            }),
            [classes, override],
        );

        return (
            <RichTextContext.Provider
                value={{
                    editor,
                    emoji,
                    mention,
                    cache: emoji?.cache() || {},
                    saveCache: handleEmojiCache,
                    setShowingMenuPopover,
                }}
            >
                <div className={classes.root}>
                    {hasTopComponent && <div {...topComponentProps}>{topComponent}</div>}
                    <InputWrapper {...inputWrapperProps}>
                        <div {...editorWrapperProps}>
                            {!!preComponent && (
                                <div className={classes.preComponent}>{preComponent}</div>
                            )}
                            <div className={classes.editorContainer}>
                                <EditorContent {...editorProps} />
                                {bottomComponent && (
                                    <div {...bottomComponentProps}>{bottomComponent}</div>
                                )}
                            </div>
                            {!withCustomToolbar && <Menu {...menuProps} />}
                            {getIcons}
                        </div>
                    </InputWrapper>
                </div>
            </RichTextContext.Provider>
        );
    },
);

RichText.overrides = ['root', 'editorWrapper', 'editor', 'topComponent', 'bottomComponent'];

RichText.propTypes = {
    className: PropTypes.string,
    /** Error will be displayed below the component with style changes */
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /** Info will be displayed below the component */
    info: PropTypes.string,
    isFullWidth: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onSubmit: PropTypes.func,
    onEsc: PropTypes.func,
    placeholder: PropTypes.string,
    toolbar: PropTypes.arrayOf(
        PropTypes.shape({
            hint: PropTypes.string,
            item: PropTypes.oneOf(['b', 'i', 'u', 'ol', 'ul', 'del']),
        }),
    ),
    toolbarStyle: PropTypes.oneOf(['floating', 'fixed']),
    /** An HTML string */
    value: PropTypes.string,
    mention: PropTypes.shape({
        /** Fetch suggestions for the mentions feature */
        fetchSuggestions: PropTypes.func,
        /** Tooltip text for the mentions button in the toolbar */
        tooltip: PropTypes.string,
        texts: PropTypes.shape({
            /** Text advice to show at the top of the mentions popover */
            advice: PropTypes.string,
            /** Text to show if the mentions search can't find a match */
            noResults: PropTypes.string,
        }),
        /** Max visible items in the suggestions list */
        maxVisibleItems: PropTypes.number,
    }),
    /** Enable compact mode: 'Single line height' editor with action buttons on the right that expands on focus */
    compactMode: PropTypes.bool,
    /** Enable autofocus */
    autofocus: PropTypes.bool,
    canSubmit: PropTypes.func,
    onChangeFocus: PropTypes.func,
    emoji: PropTypes.shape({
        /** Tooltip text for the mentions button in the toolbar */
        tooltip: PropTypes.string,
        /** Max visible items in the suggestions list */
        maxVisibleItems: PropTypes.number,
    }),
    /** Hide clear action button in the editor's input area */
    hideClear: PropTypes.bool,
    submitTooltip: PropTypes.string,
    /** No toolbar is shown, no emoji tool, just the text box and Submit button on the right */
    isBasic: PropTypes.bool,
    basicType: PropTypes.PropTypes.oneOf(['static', 'dynamic']),
    /** In basic richtext a loader can be shown instead of submit button */
    loading: PropTypes.bool,
    /** Allows direct access to the editor and set content */
    getEditorRef: PropTypes.func,
    /** Disables the submit button */
    isSubmitDisabled: PropTypes.bool,
    /** Set it to `true` if you're planning to provide a custom toolbar for the editor */
    withCustomToolbar: PropTypes.bool,
    /** Set it to `true` if you want the editor to behave like in a chat client. p.e 'Enter' submits the message */
    chatbox: PropTypes.bool,
    /** Set it to override link extension's config */
    linkConfig: PropTypes.shape({
        openOnClick: PropTypes.bool,
        autolink: PropTypes.bool,
    }),
    /** Set it to override the pre component */
    preComponent: PropTypes.node,
    /** Set it to override the top component */
    topComponent: PropTypes.node,
    /** Set it to override the bottom component */
    bottomComponent: PropTypes.node,
};

export default RichText;
